// SUB-OFFICE 7 — THE PHYSICAL BENCH (W1). Slot host: starts a duty via
// POST /api/physical/start, hands the issued task_spec to the system runner,
// collects the raw behavior log, submits it, and returns the PERFORMANCE
// verdict to the docket loop (which renders it through the ordinary slip).
// The client never sends computed results — only events and timestamps.

import { api } from '../net';
import { runConcurrence } from './concurrence';
import { runGauges } from './gauges';
import { runContrition } from './contrition';
import { runJunction } from './junction';
import { runDiscrepancy } from './discrepancy';
import { runConveyance } from './conveyance';
import { runRetention } from './retention';
import { runTerminals } from './terminals';
import { runArmature } from './armature';
import { runComposure } from './composure';
import { runObservation } from './observation';
import type { EventBus } from '../entities/entities';
import type { Player } from '../entities/player';
import type { SceneManager } from '../render/scene';
import type { Fsm } from '../state';
import type { DocketSlot, SubmitResponse } from '../../shared/types';
import type { BehaviorLog } from '../../shared/physlog';
import type {
  Form2VSpec, Form33CSpec, Form5JSpec, Form8RSpec, Form5DSpec, Form5MSpec,
  Form5SSpec, Form4TSpec, Form12KSpec, Form44SSpec, Form17OSpec,
} from '../../shared/physspec';

export interface BenchDeps {
  bus: EventBus;
  player: Player;
  sceneMgr: SceneManager;
  fsm: Fsm;
}

export type BenchOutcome =
  | { kind: 'done'; response: SubmitResponse }
  | { kind: 'abandoned' }
  | { kind: 'failed'; memo: string };

/** Route a system id to its runner. Returns `undefined` for an unknown system
 *  (no equipment installed), `null` for an abandoned run, else the BehaviorLog.
 *  Shared by the live bench and the __DEV__ bench-drive harness. */
export function dispatchRunner(
  deps: BenchDeps,
  taskId: string,
  system: string,
  task_spec: unknown
): Promise<BehaviorLog | null> | undefined {
  switch (system) {
    case 'FORM5J': return runConcurrence(deps, taskId, task_spec as Form5JSpec);
    case 'FORM2V': return runGauges(deps, taskId, task_spec as Form2VSpec);
    case 'FORM33C': return runContrition(deps, taskId, task_spec as Form33CSpec);
    case 'FORM8R': return runJunction(deps, taskId, task_spec as Form8RSpec);
    case 'FORM5D': return runDiscrepancy(deps, taskId, task_spec as Form5DSpec);
    case 'FORM5M': return runConveyance(deps, taskId, task_spec as Form5MSpec);
    case 'FORM5S': return runRetention(deps, taskId, task_spec as Form5SSpec);
    case 'FORM4T': return runTerminals(deps, taskId, task_spec as Form4TSpec);
    case 'FORM12K': return runArmature(deps, taskId, task_spec as Form12KSpec);
    case 'FORM44S': return runComposure(deps, taskId, task_spec as Form44SSpec);
    case 'FORM17O': return runObservation(deps, taskId, task_spec as Form17OSpec);
    default: return undefined;
  }
}

export class PhysicalBench {
  busy = false;

  constructor(private deps: BenchDeps) {}

  /** Run a PHYSICAL docket slot end to end. */
  async run(slot: DocketSlot): Promise<BenchOutcome> {
    if (!slot.physical) return { kind: 'failed', memo: 'The duty sheet is blank. Request your docket again.' };
    this.busy = true;
    try {
      const start = await api.physicalStart(slot.demand_id);
      if (!start.ok || !start.data) {
        return { kind: 'failed', memo: start.memo || 'The bench declined to open the duty.' };
      }
      const { task_id, system, task_spec } = start.data;
      this.deps.fsm.to('PHYSICAL_TASK');

      const log = await dispatchRunner(this.deps, task_id, system, task_spec);
      if (log === undefined) {
        return { kind: 'failed', memo: `Duty ${system} awaits equipment the hall is still installing.` };
      }
      if (!log) return { kind: 'abandoned' };

      const res = await api.physicalSubmit(slot.demand_id, task_id, log);
      if (!res.ok || !res.data) {
        return { kind: 'failed', memo: res.memo || 'The performance record did not reconcile. The duty remains open.' };
      }
      return { kind: 'done', response: res.data };
    } finally {
      this.busy = false;
    }
  }
}
